import React, { memo, useEffect } from 'react';
import {
  Route,
  Switch,
  RouteComponentProps,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider } from 'emotion-theming';

import Header from 'components/Header';
import Footer from 'components/Footer';
// import GDPR from 'components/GDPR';
import UserBucket from 'components/UserBucket';

import statusCodes from 'constants/statusCodes';
import routes from 'routeConfig';
import Boot from 'shared/components/Boot';
import BaseStyles from 'shared/styles/BaseStyles';
import VP from 'shared/components/VisitorPrioritization';
import InitializeExperiment from 'shared/components/Experiment/Initialize';
import InitializeAnalytics from 'shared/components/Analytics/Initialize';
import BackToTop from './BackToTop';

import { getAppTheme } from './theme';
import styled from './styled';
import { setSuperProperty } from 'helpers/utils/user';
import { useSelector } from 'react-redux';
import { State } from 'store/types';
import ChatButton from './ChatButton';
import QrAppBanner from './QrAppBanner';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { AB_GETAPP_DWEB } from 'constants/experiments';

interface PageRoute {
  path: string;
  component: any;
  routes?: [object];
  status?: number;
  exact?: boolean;
}

const BodyWrapper = styled.div`
  -webkit-font-smoothing: antialiased;
`;

const RoutesWithSubRoutes = (route: PageRoute) => (
  <Route
    path={route.path}
    exact={route.exact}
    render={(props: RouteComponentProps) => {
      if (route.status === statusCodes.NOT_FOUND && props.staticContext) {
        props.staticContext.statusCode = route.status;
      }
      return (
        <route.component
          routes={route.routes} // needed for nested routes
          {...props}
        />
      );
    }}
    fallback={() => <div>Loading... main</div>}
  />
);

const shouldShowAppBanner = (pathname: string, isAppBannerEnabled: boolean) =>
  (pathname === '/' || pathname === '/home') && isAppBannerEnabled;
const App = () => {
  const isZendeskEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.zendeskChatEnabled || false
  );
  const { pathname } = useSelector((state: State) => state.appReducer);
  const { templateData } =
    useSelector((state: State) => state.snsConfig.data) || {};
  const customTheme = templateData?.theme;
  const theme = getAppTheme(customTheme);
  useEffect(() => {
    setSuperProperty();
  }, []);

  let location = useLocation();
  const { hash } = location;
  if (pathname && pathname.indexOf('/lp') > 0) {
    location.pathname = pathname;
    if (!__SERVER__) {
      const updatedPath = pathname + location?.search + hash;
      window.history.replaceState({}, '', updatedPath);
    }
  }
  const downloadAppBannerConfig = useSelector(
    (state: State) => state.remoteConfig.configs.downloadAppBannerConfig
  );
  const isAppBannerEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_GETAPP_DWEB, 'A')
  );
  let showAppBanner = shouldShowAppBanner(
    location.pathname,
    isAppBannerEnabled
  );
  if (!showAppBanner || !downloadAppBannerConfig) showAppBanner = false;
  return (
    <>
      <ThemeProvider theme={theme}>
        <BaseStyles />
        <Boot />
        <VP />
        <Header />
        <BodyWrapper>
          <Switch>
            {routes.map((route, index: number) => (
              <RoutesWithSubRoutes
                key={index}
                {...route}
              />
            ))}
          </Switch>
        </BodyWrapper>
        {showAppBanner && (
          <QrAppBanner
            qrCodeUrl={downloadAppBannerConfig.qrCodeUrl}
            title={downloadAppBannerConfig.title}
            subtitle={downloadAppBannerConfig.subtitle}
            clearStorageTimeout={downloadAppBannerConfig.clearStorageTimeout}
            closeCountThreshold={downloadAppBannerConfig.closeCountThreshold}
          />
        )}
        <Footer />
        {isZendeskEnabled && <ChatButton />}
        <BackToTop />
        {/* <GDPR /> */}
        <UserBucket />
        <InitializeAnalytics />
        <InitializeExperiment />
      </ThemeProvider>
    </>
  );
};

export default memo(App);
